import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useMutationAppNavigationSettings } from "libs/api/src/lib/hooks/club/useMutationAppNavigationSettings";
import { AnimatePresence, motion } from "motion/react";
import { useContext, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { useClubSettings } from "../../../providers";
import useGymflowModels from "../../../store";
import { Button, EyeIcon, Switch } from "../../atoms";
import { XIcon } from "../../atoms/icons/XIcon";
import { AppSettingsContext } from "./AppSettingsContext";
import { AppSettingsPreview } from "./AppSettingsPreview";

export const AppSettingsNavigation = () => {
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { clubId } = useClubSettings();
  const { mutateAsync: updateAppNavigation } = useMutationAppNavigationSettings(
    { api, clubId },
  );
  const [parent] = useAutoAnimate();
  const { navigationLocalData, setNavigationLocalData } =
    useContext(AppSettingsContext);
  const [showPreview, setShowPreview] = useState(false);
  return (
    <>
      {createPortal(
        <AnimatePresence>
          {showPreview && (
            <motion.div
              className="absolute inset-0 z-50 flex flex-col items-center justify-start bg-gray-100 pt-4 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="z-10 flex w-full flex-row items-center justify-between gap-4 p-4 pb-0">
                <div className="text-xl font-semibold text-gray-950">
                  {t("pages.settings.appSettings.navigation.preview")}
                </div>
                <Button
                  intent="transparent"
                  onClick={() => {
                    setShowPreview(false);
                  }}
                >
                  <XIcon className="stroke-gray-950" />
                </Button>
              </div>
              <AppSettingsPreview
                imageClassName="max-h-[calc(100vh-12rem)]"
                className="pt-0"
              />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
      <div className="flex size-full flex-col justify-between gap-6">
        <div className="flex flex-col gap-6 pt-2" ref={parent}>
          <div className="flex flex-col gap-1">
            <div className="text-lg font-semibold text-gray-950">
              {t("page.settings.appSettings.navigation.title")}
            </div>
            <div className="text-sm font-medium text-gray-500">
              {t("page.settings.appSettings.navigation.description")}
            </div>
          </div>
          {navigationLocalData && (
            <div className="flex flex-col gap-2">
              <Option.Container>
                <Switch value={true} onChange={() => {}} disabled />
                <Option.Label>
                  {t("page.settings.appSettings.navigation.homePage")}
                </Option.Label>
              </Option.Container>
              <Option.Container>
                <Switch
                  value={navigationLocalData.classesNavigationEnabled}
                  onChange={() => {
                    setNavigationLocalData({
                      ...navigationLocalData,
                      classesNavigationEnabled:
                        !navigationLocalData.classesNavigationEnabled,
                    });
                  }}
                />
                <Option.Label>
                  {t("page.settings.appSettings.navigation.classes")}
                </Option.Label>
              </Option.Container>
              <Option.Container>
                <Switch
                  value={navigationLocalData.appointmentsNavigationEnabled}
                  onChange={() => {
                    setNavigationLocalData({
                      ...navigationLocalData,
                      appointmentsNavigationEnabled:
                        !navigationLocalData.appointmentsNavigationEnabled,
                    });
                  }}
                />
                <Option.Label>
                  {t("page.settings.appSettings.navigation.appointments")}
                </Option.Label>
              </Option.Container>
              <Option.Container>
                <Switch
                  value={navigationLocalData.shopNavigationEnabled}
                  onChange={() => {
                    setNavigationLocalData({
                      ...navigationLocalData,
                      shopNavigationEnabled:
                        !navigationLocalData.shopNavigationEnabled,
                    });
                  }}
                />
                <Option.Label>
                  {t("page.settings.appSettings.navigation.shop")}
                </Option.Label>
              </Option.Container>
              <Option.Container>
                <Switch value={true} onChange={() => {}} disabled />
                <Option.Label>
                  {t("page.settings.appSettings.navigation.profile")}
                </Option.Label>
              </Option.Container>
            </div>
          )}
        </div>
        <div className="-mx-6 -mb-6 flex flex-row justify-end gap-2 border-t border-gray-300 px-6 py-4">
          <Button
            className="w-1/2 gap-1 lg:hidden"
            onClick={async () => {
              setShowPreview(true);
            }}
          >
            <EyeIcon className="stroke-gray-950" />
            {t("pages.settings.appSettings.navigation.preview")}
          </Button>
          <Button
            intent="secondary"
            className="max-lg:w-1/2 lg:px-12"
            onClick={async () => {
              if (navigationLocalData) {
                await updateAppNavigation(navigationLocalData);
              }
            }}
          >
            {t("portal.common.save")}
          </Button>
        </div>
      </div>
    </>
  );
};

const Option = {
  Container: ({ children }: { children: React.ReactNode }) => (
    <div className="flex flex-row items-center gap-2 rounded-xl border border-gray-200 p-3">
      {children}
    </div>
  ),
  Label: ({ children }: { children: React.ReactNode }) => (
    <div className="text-sm font-medium text-gray-500">{children}</div>
  ),
};
